import { useState } from "react";
import { useParams } from "react-router";
import { useTheme } from "@mui/material";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { useToken } from "src/business-hooks/useToken";
import {
  useDeleteAdminTestsMutation,
  useDeleteEnvAlertsMutation,
  useSendDailyIncidentReportMutation,
  useSendMonthlySlaReportMutation,
  useSimulateAlertsMutation,
} from "src/queries/admin-actions/admin-actions.mutation";
import {
  type RunInsightsScope,
  useRunInsightsMutation,
} from "src/queries/admin-actions/single-admin-actions.mutation";
import { useQueryMinimalAppInfo } from "src/queries/apps/apps.query";
import { useDeleteEnvMutation } from "src/queries/env/env.mutation";
import { useQueryEnvs } from "src/queries/env/env.query";
import { resetAllLocalPrefs } from "src/storage/createLocalPrefs";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { notify } from "@utils/notification.ts";

const LOGOUT_RESET_DELAY_MS = 300;

/** Menu anchor + open state for the debug settings dropdown. */
export function useTopbarDebugMenuAnchor() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const close = () => {
    setAnchorEl(null);
  };
  return { anchorEl, setAnchorEl, open, close };
}

/** Shared section label styles for debug menu group headings. */
export function useTopbarDebugSectionTitleSx() {
  const theme = useTheme();
  return {
    ml: 2,
    fontSize: "12px",
    color: theme.palette.grey[600],
  };
}

/** Logout + local storage reset, and URL search/hash reset (user-facing debug utilities). */
export function useTopbarDebugUserSessionActions() {
  const { logout } = useToken();
  const resetAll = useGlobalLocalContext((state) => state.resetAll);

  const resetSessionAndStorage = () => {
    void logout();
    setTimeout(() => {
      resetAll();
    }, LOGOUT_RESET_DELAY_MS);
  };

  const resetUrlSearchAndHash = () => {
    const u = new URL(window.location.href);
    u.search = "";
    u.hash = "";
    window.history.pushState({}, "", u.toString());
  };

  return {
    resetSessionAndStorage,
    resetUrlSearchAndHash,
    resetLocalPrefs: resetAllLocalPrefs,
  };
}

export type TopbarTenantAdminDebugActions = {
  deleteEnv: () => void;
  deleteAllAlerts: () => void;
  sendDailyIncidentsMail: () => Promise<void>;
  runInsights: () => Promise<void>;
  runInsightsForTenant: () => Promise<void>;
  runInsightsForEnv: () => Promise<void>;
  runInsightsForApp: () => Promise<void>;
  /** Whether the current route carries the context each scoped run needs. */
  canScopeTenant: boolean;
  canScopeEnv: boolean;
  canScopeApp: boolean;
  sendMonthlySlaEmail: () => Promise<void>;
  simulateAlerts: () => void;
  deleteAllTests: () => void;
  deleteEnvAutoTests: () => void;
};

/** Tenant-admin env-scoped debug actions (mutations wired for Topbar). */
export function useTopbarTenantAdminDebugActions(): TopbarTenantAdminDebugActions {
  const { env_name, app_name } = useParams();
  const { tenant_id } = useCurrentUser();
  const deleteEnvMutation = useDeleteEnvMutation();
  const deleteEnvAlertsMutation = useDeleteEnvAlertsMutation();
  const sendDailyIncidentReportMutation = useSendDailyIncidentReportMutation();
  const runInsightsMutation = useRunInsightsMutation();
  const sendMonthlySlaReportMutation = useSendMonthlySlaReportMutation();
  const simulateAlertsMutation = useSimulateAlertsMutation();
  const deleteAdminTestsMutation = useDeleteAdminTestsMutation();

  // Resolve the current route's env/app to numeric ids for scoped runs.
  const { data: envs } = useQueryEnvs();
  const { data: appInfo } = useQueryMinimalAppInfo({
    enabled: Boolean(env_name && app_name),
  });
  const envId = appInfo?.env_id ?? envs?.find((e) => e.env === env_name)?.env_id;
  const appId = appInfo?.app_id;

  const runInsightsScoped = async (scope: RunInsightsScope, msg: string) => {
    try {
      await runInsightsMutation.mutateAsync(scope);
      notify(msg, { variant: "success" });
    } catch {
      // dfetch already notifies on failure
    }
  };

  return {
    runInsightsForTenant: async () => {
      if (tenant_id !== undefined) {
        await runInsightsScoped(
          { tenant_id },
          `Insights queued for tenant ${tenant_id}`
        );
      }
    },
    runInsightsForEnv: async () => {
      if (envId !== undefined) {
        await runInsightsScoped(
          { env_id: envId },
          `Insights queued for env ${env_name}`
        );
      }
    },
    runInsightsForApp: async () => {
      if (appId !== undefined) {
        await runInsightsScoped(
          { app_id: appId },
          `Insights queued for app ${app_name}`
        );
      }
    },
    canScopeTenant: tenant_id !== undefined,
    canScopeEnv: envId !== undefined,
    canScopeApp: appId !== undefined,
    deleteEnv: () => {
      if (env_name) {
        deleteEnvMutation.mutate(env_name);
      }
    },
    deleteAllAlerts: () => {
      if (env_name) {
        deleteEnvAlertsMutation.mutate(env_name);
      }
    },
    sendDailyIncidentsMail: async () => {
      try {
        await sendDailyIncidentReportMutation.mutateAsync();
      } catch {
        // dfetch already notifies on failure
      }
    },
    runInsights: async () => {
      try {
        await runInsightsMutation.mutateAsync(undefined);
        notify("insights ran successfully", { variant: "success" });
      } catch {
        // dfetch already notifies on failure
      }
    },
    sendMonthlySlaEmail: async () => {
      try {
        await sendMonthlySlaReportMutation.mutateAsync();
        notify("Monthly email sent", { variant: "success" });
      } catch (error) {
        notify("Failed sending monthly SLA report email", {
          variant: "error",
          error,
        });
      }
    },
    simulateAlerts: () => {
      if (env_name) {
        simulateAlertsMutation.mutate({ env_name });
      }
    },
    deleteAllTests: () => {
      if (env_name) {
        deleteAdminTestsMutation.mutate({ env_name, is_auto: false });
      }
    },
    deleteEnvAutoTests: () => {
      if (env_name) {
        deleteAdminTestsMutation.mutate({ env_name });
      }
    },
  };
}
